// Unified Supabase Auth Context for Mudo (Email + Password + Magic Link)

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { SUPABASE_URL_ORIGIN, supabase } from "../supabaseClient";
import { APP_BASE_URL } from "@/lib/env";
import { buildPromoState } from "../lib/promo/promoUtils";

const AuthCtx = createContext(null);
const isDev = import.meta?.env?.MODE === "development";
const debugAuthLog = (...args) => {
  if (isDev) console.log("[AUTH]", ...args);
};

const AUTH_INIT_TIMEOUT_MS = 5000;

const AUTH_REFRESH_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
const AUTH_REFRESH_COOLDOWN_MS = 30 * 1000; // 30 seconds

// Single-flight bootstrap to prevent multiple getSession() calls per tab
let authBootstrapInFlight = null;
let authSubscriptionUnsubscribe = null;
const authMetrics = { bootstrapStartMs: 0, lastEventMs: 0, eventCount: 0, softTimeoutFiredMs: 0 };
const logAuthMetric = (type, data) => {
  if (typeof process === "undefined" || process.env?.NODE_ENV === "production") return;
  authMetrics.lastEventMs = Date.now();
  authMetrics.eventCount++;
  console.log(`[AUTH:${type}]`, data);
};

const refreshInFlightRef = { current: null };
const authOutageUntilRef = { current: 0 };

function getExpiresAtMs(session) {
  const expiresAt = session?.expires_at;
  if (!expiresAt) return null;
  return expiresAt * 1000;
}

function isExpiringSoon(session) {
  const expiresAtMs = getExpiresAtMs(session);
  if (!expiresAtMs) return false;
  return expiresAtMs - Date.now() <= AUTH_REFRESH_WINDOW_MS;
}

function isRefreshNetworkishFailure(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  const status = Number(err?.status || err?.statusCode || err?.code || 0);
  return (
    status === 522 ||
    msg.includes("failed to fetch") ||
    msg.includes("timeout") ||
    msg.includes("522")
  );
}

// Detect invalid refresh token errors that require auto-signout
export function isInvalidRefreshTokenError(err) {
  const msg = String(err?.message || "").toLowerCase();
  const name = String(err?.name || "").toLowerCase();
  return (
    name.includes("authapierror") &&
    (msg.includes("invalid refresh token") || msg.includes("refresh token not found"))
  );
}

function is401RetryReason(reason) {
  const r = String(reason || "").toLowerCase();
  return r.includes("401") || r.includes("unauthorized") || r.includes("auth retry");
}

// On-demand refresh: used by AuthContext and API callers (e.g. apiFetch retry-on-401).
export async function ensureFreshSession({ reason } = {}) {
  if (!supabase) return null;

  const now = Date.now();
  if (now < authOutageUntilRef.current) {
    const err = new Error(
      "Auth is temporarily unavailable. Please wait a moment and try again."
    );
    err.code = "AUTH_REFRESH_COOLDOWN";
    throw err;
  }

  // Prefer the shared snapshot (kept current via onAuthStateChange), but fall back to supabase.
  let currentSession = sharedAuthState.snapshot?.session ?? null;
  let currentUser = sharedAuthState.snapshot?.user ?? null;

  if (!currentSession) {
    try {
      const { data } = await supabase.auth.getSession();
      currentSession = data?.session ?? null;
      currentUser = data?.session?.user ?? null;
    } catch {
      currentSession = null;
      currentUser = null;
    }
  }

  if (!currentSession || !currentUser) return null;
  const force = is401RetryReason(reason);
  if (!force && !isExpiringSoon(currentSession)) return currentSession;

  if (refreshInFlightRef.current) return refreshInFlightRef.current;

  refreshInFlightRef.current = (async () => {
    try {
      debugAuthLog("ensureFreshSession: refreshing", {
        reason: reason || null,
        supabaseOrigin: SUPABASE_URL_ORIGIN,
        onLine: typeof navigator !== "undefined" ? navigator.onLine : null,
      });

      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;

      const nextSession = data?.session ?? null;
      if (!nextSession) throw new Error("Session refresh returned no session");

      sharedAuthState.snapshot = {
        session: nextSession,
        user: nextSession?.user ?? null,
        isAuthReady: true,
      };
      sharedAuthState.didInit = true;

      return nextSession;
    } catch (err) {
      // Handle invalid refresh token: auto-signout + clear storage + redirect + toast
      if (isInvalidRefreshTokenError(err)) {
        debugAuthLog("ensureFreshSession: invalid refresh token detected, auto-signout", err.message);
        
        // Sign out from Supabase
        try {
          await supabase.auth.signOut();
        } catch (signOutErr) {
          console.warn("[Auth] signOut during invalid refresh token failed", signOutErr);
        }
        
        // Clear app localStorage keys
        try {
          if (typeof localStorage !== "undefined") {
            // Clear all Mudo-specific keys
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && (key.startsWith("mudo") || key.startsWith("sb-"))) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach((key) => {
              try {
                localStorage.removeItem(key);
              } catch {}
            });
          }
        } catch {}
        
        // Clear shared auth state
        sharedAuthState.snapshot = {
          session: null,
          user: null,
          isAuthReady: true,
        };
        sharedAuthState.didInit = true;
        
        // Show friendly toast notification
        if (typeof window !== "undefined") {
          // Dispatch a custom event for toast notification
          try {
            window.dispatchEvent(
              new CustomEvent("mudo:toast", {
                detail: {
                  message: "Session expired. Please sign in again.",
                  tone: "info",
                  ttl: 3000,
                },
              })
            );
          } catch {}
          
          // Redirect to /start (visitor home)
          setTimeout(() => {
            try {
              window.location.href = "/start";
            } catch {}
          }, 100);
        }
        
        // Throw error so caller knows session is invalid
        const friendly = new Error("Session expired. Please sign in again.");
        friendly.code = "INVALID_REFRESH_TOKEN";
        throw friendly;
      }
      
      if (isRefreshNetworkishFailure(err)) {
        authOutageUntilRef.current = Date.now() + AUTH_REFRESH_COOLDOWN_MS;
        const friendly = new Error(
          "Network/auth service is temporarily unavailable. Please wait 30 seconds and try again."
        );
        friendly.code = "AUTH_REFRESH_FAILED";
        throw friendly;
      }
      throw err;
    } finally {
      refreshInFlightRef.current = null;
    }
  })();

  return refreshInFlightRef.current;
}

// Module-scope snapshot so StrictMode remounts don't re-trigger getSession().
// (StrictMode mounts, unmounts, then mounts again in dev.)
const sharedAuthState = {
  initPromise: null,
  didInit: false,
  snapshot: {
    session: null,
    user: null,
    isAuthReady: false,
  },
};

/**
 * Get current user from centralized auth state (non-React safe).
 * Use this instead of supabase.auth.getUser() in library/API functions.
 * Returns null if not authenticated or auth not ready.
 */
export function getCachedUser() {
  return sharedAuthState.snapshot?.user ?? null;
}

/**
 * Get current session from centralized auth state (non-React safe).
 * Use this instead of supabase.auth.getSession() in library/API functions.
 * Returns null if not authenticated or auth not ready.
 */
export function getCachedSession() {
  return sharedAuthState.snapshot?.session ?? null;
}

/**
 * Check if auth state is ready (non-React safe).
 * Use this to avoid acting on stale/null data during boot.
 */
export function isAuthReady() {
  return sharedAuthState.snapshot?.isAuthReady === true;
}

async function initAuthOnce() {
  if (sharedAuthState.didInit) return sharedAuthState.snapshot;
  if (authBootstrapInFlight) return authBootstrapInFlight;

  const bootstrapStartMs = Date.now();
  authMetrics.bootstrapStartMs = bootstrapStartMs;
  logAuthMetric("BOOTSTRAP_START", { timestamp: new Date(bootstrapStartMs).toISOString() });

  authBootstrapInFlight = (async () => {
    if (!supabase) {
      sharedAuthState.snapshot = { session: null, user: null, isAuthReady: true };
      sharedAuthState.didInit = true;
      return sharedAuthState.snapshot;
    }

    let restoredSession = null;
    let restoredUser = null;

    try {
      const startedAt = (globalThis?.performance?.now?.() ?? Date.now());

      const getSessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error(`getSession timeout after ${AUTH_INIT_TIMEOUT_MS}ms`)),
          AUTH_INIT_TIMEOUT_MS
        )
      );

      const result = await Promise.race([getSessionPromise, timeoutPromise]);
      const elapsedMs = Math.round((globalThis?.performance?.now?.() ?? Date.now()) - startedAt);
      const data = result?.data;
      const error = result?.error;

      logAuthMetric("BOOTSTRAP_RESOLVED", {
        elapsedMs,
        hasSession: !!data?.session,
        hasError: !!error,
      });

      if (error) {
        // Supabase returned a response error; treat as non-fatal.
        console.warn("[Auth] getSession error (non-fatal)", error.message);
      }

      restoredSession = data?.session ?? null;
      restoredUser = data?.session?.user ?? null;
    } catch (err) {
      // Network/CORS/timeout errors must never block boot.
      const msg = err?.message || String(err);
      const errName = String(err?.name || "").toLowerCase();
      
      // Detect refresh token failures (CORS/522/network errors during token refresh)
      const isRefreshFailure = 
        errName.includes("authretryfetcherror") ||
        errName.includes("authretryablefetcherror") ||
        msg.includes("failed to fetch") ||
        msg.includes("522") ||
        msg.includes("cors") ||
        msg.includes("refresh_token");
      
      if (isRefreshFailure) {
        console.warn("[Auth] Token refresh failed during getSession (CORS/network issue), clearing stale session", {
          errorName: err?.name,
          message: msg,
          onLine: typeof navigator !== "undefined" ? navigator.onLine : null,
          supabaseOrigin: SUPABASE_URL_ORIGIN,
        });
        
        // Clear the corrupted session from storage immediately
        try {
          await supabase.auth.signOut();
        } catch (signOutErr) {
          debugAuthLog("signOut during refresh failure failed", signOutErr);
        }
        
        // Clear localStorage auth keys
        try {
          if (typeof localStorage !== "undefined") {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith("sb-")) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach((key) => {
              try {
                localStorage.removeItem(key);
              } catch {}
            });
          }
        } catch {}
        
        // Don't block boot - just clear the session
        restoredSession = null;
        restoredUser = null;
      } else if (String(msg).toLowerCase().includes("timeout")) {
        console.warn("[Auth] getSession timed out (non-fatal)", {
          onLine: typeof navigator !== "undefined" ? navigator.onLine : null,
          supabaseOrigin: SUPABASE_URL_ORIGIN,
        });
      } else {
        console.warn("[Auth] getSession failed (network/outage) (non-fatal)", msg);
      }
    }

    sharedAuthState.snapshot = {
      session: restoredSession,
      user: restoredUser,
      isAuthReady: true,
    };
    sharedAuthState.didInit = true;
    return sharedAuthState.snapshot;
  })().finally(() => {
    authBootstrapInFlight = null;
  });

  return authBootstrapInFlight;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authDegraded, setAuthDegraded] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  const [profile, setProfile] = useState(null);
  const [studioProfile, setStudioProfile] = useState(null);
  const [visitorProfile, setVisitorProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);

  const emailConfirmationRedirectTo = useMemo(() => {
    try {
      return (
        import.meta.env.VITE_SUPABASE_REDIRECT_URL ||
        `${(APP_BASE_URL || window.location.origin)}/auth/callback`
      );
    } catch {
      return "/auth/callback";
    }
  }, []);

  const passwordResetRedirectTo = useMemo(() => {
    try {
      const base = APP_BASE_URL || window.location.origin;
      return `${base}/auth/reset`;
    } catch {
      return "/auth/reset";
    }
  }, []);

  const userNeedsEmailConfirmation = (u) => !u?.email_confirmed_at;

  const forceSignOutOnAuthError = useCallback(
    async (err) => {
      console.warn("[Auth] forceSignOutOnAuthError", err);
      debugAuthLog("forceSignOutOnAuthError", {
        err: err?.message || err,
        currentUserId: user?.id || null,
      });
      setAuthError(err);
      setSession(null);
      setUser(null);
      setProfile(null);
      setStudioProfile(null);
      setVisitorProfile(null);
      setUserProfile(null);
      setOnboardingStatus(null);
      try {
        await supabase?.auth?.signOut();
      } catch (e) {
        console.warn("[Auth] signOut after auth error failed", e);
      }
    },
    []
  );

  const isRefreshError = (err) => {
    const msg = String(err?.message || "").toLowerCase();
    return (
      msg.includes("invalid_grant") ||
      msg.includes("invalid grant") ||
      msg.includes("refresh_token")
    );
  };

  const subscriptionRef = useRef(null);
  const authListenerInitRef = useRef(false);
  const signInInFlightRef = useRef(null);
  const lastBootUserIdRef = useRef(null);
  const softTimeoutFiredRef = useRef(false);
  const authDegradedRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    const applySnapshot = (snap) => {
      if (!mounted) return;
      setSession(snap?.session ?? null);
      const snapUserId = snap?.user?.id ?? null;
      lastBootUserIdRef.current = snapUserId;
      setUser(snap?.user ?? null);
      setAuthLoading(false);
      setIsAuthReady(true);
    };

    // Soft timeout: unblock UI after 1500ms even if auth hasn't resolved yet
    const softTimeoutId = setTimeout(() => {
      if (!mounted) return;
      if (sharedAuthState.snapshot.isAuthReady) return; // Already ready
      
      softTimeoutFiredRef.current = true;
      authMetrics.softTimeoutFiredMs = Date.now();
      logAuthMetric("SOFT_TIMEOUT_FIRED", {
        delayMs: authMetrics.softTimeoutFiredMs - authMetrics.bootstrapStartMs,
      });
      debugAuthLog("Soft timeout: unblocking UI after 1500ms, auth still pending");
      
      // Set authReady true but keep user/session null until actual data arrives
      setAuthLoading(false);
      setIsAuthReady(true);
      // Don't modify sharedAuthState here - let actual session update it later
    }, 1500);

    // Auth degraded check: if still no user after 3000ms, mark auth as degraded
    const authDegradedId = setTimeout(() => {
      if (!mounted || authDegradedRef.current) return;
      if (sharedAuthState.snapshot?.user) return; // User arrived, no degradation
      
      authDegradedRef.current = true;
      const elapsedMs = Date.now() - authMetrics.bootstrapStartMs;
      logAuthMetric("AUTH_DEGRADED", {
        elapsedMs,
        visibilityState: typeof document !== "undefined" ? document.visibilityState : "unknown",
        pathname: typeof window !== "undefined" ? window.location.pathname : "unknown",
      });
      debugAuthLog("Auth degraded: no user/session after 3000ms");
      setAuthDegraded(true);
    }, 3000);

    // Fast-path: if we already initialized in this JS runtime, reuse snapshot
    if (sharedAuthState.snapshot.isAuthReady) {
      clearTimeout(softTimeoutId);
      clearTimeout(authDegradedId);
      applySnapshot(sharedAuthState.snapshot);
    } else if (mounted) {
      setAuthLoading(true);
      setIsAuthReady(false);
    }

    initAuthOnce()
      .then((snap) => {
        clearTimeout(softTimeoutId);
        clearTimeout(authDegradedId);
        logAuthMetric("BOOTSTRAP_COMPLETE", {
          hasSession: !!snap?.session,
          userId: snap?.user?.id || null,
          softTimeoutFired: softTimeoutFiredRef.current,
          authDegraded: authDegradedRef.current,
          totalMs: Date.now() - authMetrics.bootstrapStartMs,
        });
        debugAuthLog("init: auth ready", {
          hasSession: !!snap?.session,
          userId: snap?.user?.id || null,
          softTimeoutFired: softTimeoutFiredRef.current,
        });
        applySnapshot(snap);
      })
      .catch(() => {
        clearTimeout(softTimeoutId);
        clearTimeout(authDegradedId);
        // Should be rare; still unblock app.
        applySnapshot({ session: null, user: null, isAuthReady: true });
      });

    // Listen for auth state changes (setup regardless of getSession success)
    // Idempotent + always cleaned up.
    if (supabase && mounted && !authListenerInitRef.current) {
      try {
        const { data } = supabase.auth.onAuthStateChange((_event, newSession) => {
          const nextSession = newSession ?? null;
          const nextUser = newSession?.user ?? null;
          const nextUserId = nextUser?.id ?? null;

          // Keep shared snapshot updated so remounts don't re-init.
          sharedAuthState.snapshot = {
            session: nextSession,
            user: nextUser,
            isAuthReady: true,
          };
          sharedAuthState.didInit = true;

          if (!mounted) return;
          logAuthMetric("AUTH_STATE_CHANGE", {
            event: _event,
            userId: nextUserId || null,
          });
          debugAuthLog("onAuthStateChange", _event, {
            userId: nextUserId || null,
          });

          // Always update session; it legitimately changes on refresh.
          setSession(nextSession);

          // Only update user state when identity changes.
          // This prevents downstream "boot" cascades on TOKEN_REFRESHED spam.
          if (!nextUserId) {
            lastBootUserIdRef.current = null;
            setUser(null);
          } else if (lastBootUserIdRef.current !== nextUserId) {
            lastBootUserIdRef.current = nextUserId;
            setUser(nextUser);
          }

          setAuthLoading(false);
          setIsAuthReady(true);
        });
        subscriptionRef.current = data?.subscription;
        authListenerInitRef.current = true;
        if (!authSubscriptionUnsubscribe) authSubscriptionUnsubscribe = data?.subscription;
      } catch (listenerErr) {
        console.error("[AUTH] Failed to set up auth state listener", listenerErr);
      }
    }

    return () => {
      mounted = false;
      clearTimeout(softTimeoutId);
      clearTimeout(authDegradedId);
      try {
        subscriptionRef.current?.unsubscribe();
      } catch {}
      subscriptionRef.current = null;
      authListenerInitRef.current = false;
    };
  }, []);

  const fetchProfiles = useCallback(async (userId) => {
    if (!supabase || !userId) {
      setProfile(null);
      setStudioProfile(null);
      setVisitorProfile(null);
      setUserProfile(null);
      setOnboardingStatus(null);
      return;
    }

    try {
      const uid = userId;

      const fetchUserProfileRow = async () => {
        // Schema variance tolerance:
        // - Some environments use `id` (uuid PK) == auth user id
        // - Others use `user_id` (uuid FK)
        // Also avoid selecting specific columns to prevent 400s when schema differs.
        const tryBy = async (col) =>
          await supabase.from("user_profiles").select("*").eq(col, uid).maybeSingle();

        const first = await tryBy("id");
        if (!first?.error) return first;

        const status = Number(first?.status);
        const msg = String(first?.error?.message || "");
        const shouldRetry = status === 400 || msg.toLowerCase().includes("column") || msg.toLowerCase().includes("unknown");
        if (!shouldRetry) return first;

        return await tryBy("user_id");
      };

      const [baseRes, visitorRes, publicUserRes, studioRes, onboardingRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id,username,display_name,avatar_url,city,bio,created_at,updated_at")
          .eq("id", uid)
          .maybeSingle(),

        supabase
          .from("visitor_profiles")
          .select("id,user_id,display_name,country,preferred_practices,created_at,updated_at")
          .eq("user_id", uid)
          .maybeSingle(),

        supabase
          .from("users_public")
          .select(
            "id,display_name,country,trial_started_at,trial_ends_at,access_until,marketing_opt_in,created_at"
          )
          .eq("id", uid)
          .maybeSingle(),

        supabase
          .from("studios")
          .select(
            "id,name,slug,city,country,is_public,owner_user_id,hero_image_url,created_at,stripe_account_id_resolved,stripe_account_id,stripe_onboarding_complete,stripe_charges_enabled,stripe_payouts_enabled"
          )
          .eq("owner_user_id", uid)
          .maybeSingle(),
      ]);

      const onboardingRes2 = await fetchUserProfileRow();

      // maybeSingle() can still surface 406 in some PostgREST configs
      const tolerate = (r) => {
        if (r?.error && Number(r?.status) !== 406) throw r.error;
        return r?.data ?? null;
      };

      const base = tolerate(baseRes);
      let visitor = tolerate(visitorRes);
      const publicUser = tolerate(publicUserRes);
      const studio = tolerate(studioRes);
      const onboarding = tolerate(onboardingRes2);

      // Optional: bootstrap visitor profile on first login if missing (best-effort; respects RLS)
      if (!visitor) {
        try {
          const insertPayload = {
            user_id: uid,
            display_name:
              base?.display_name ?? publicUser?.display_name ?? "New Visitor",
            country: publicUser?.country ?? null,
          };
          const { error: insErr } = await supabase
            .from("visitor_profiles")
            .insert(insertPayload);
          if (!insErr) {
            const { data: nextVisitor } = await supabase
              .from("visitor_profiles")
              .select("id,user_id,display_name,country,preferred_practices,created_at,updated_at")
              .eq("user_id", uid)
              .maybeSingle();
            visitor = nextVisitor ?? null;
          }
        } catch (e) {
          if (isDev) console.warn("[Auth] visitor_profiles bootstrap skipped:", e);
        }
      }

      // Merge order: users_public + profiles + visitor_profiles
      setProfile({ ...(publicUser || {}), ...(base || {}), ...(visitor || {}) });
      setStudioProfile(studio);
      setVisitorProfile(visitor);
      setUserProfile(onboarding);
      setOnboardingStatus(onboarding?.onboarding_status || null);
    } catch (err) {
      if (isDev) console.warn("[Auth] Profile fetch error:", err);
      setProfileError(err);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setProfile(null);
      setStudioProfile(null);
      setVisitorProfile(null);
      setUserProfile(null);
      setOnboardingStatus(null);
      return;
    }
    fetchProfiles(user.id);
  }, [user?.id, fetchProfiles]);

  // Allow other parts of the app to trigger a profile refresh (e.g., after applying promo)
  useEffect(() => {
    const onRefresh = () => {
      if (user?.id) fetchProfiles(user.id);
    };
    window.addEventListener("mudo:profile-refresh", onRefresh);
    return () => window.removeEventListener("mudo:profile-refresh", onRefresh);
  }, [user?.id, fetchProfiles]);

  const upsertUserProfile = useCallback(
    async (payload) => {
      if (!user?.id) throw new Error("No logged-in user");
      if (!supabase) throw new Error("Supabase not configured");
      setAuthError(null);

      const upsertPayload = {
        id: user.id,
        ...payload,
      };

      const { data, error } = await supabase
        .from("user_profiles")
        .upsert(upsertPayload, { onConflict: "id" })
        .select()
        .maybeSingle();

      if (error) {
        setAuthError(error);
        throw error;
      }

      setUserProfile(data);
      setOnboardingStatus(data?.onboarding_status || null);
      return data;
    },
    [user?.id]
  );

  const signUp = useCallback(
    async ({ email, password, role }) => {
      if (!supabase) throw new Error("Supabase not configured");
      setAuthError(null);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: emailConfirmationRedirectTo,
          data: role ? { role } : undefined,
        },
      });

      if (error) {
        setAuthError(error);
        throw error;
      }

      if (data?.session) {
        try {
          await supabase.auth.signOut();
        } catch (signOutErr) {
          console.warn("[Auth] signOut after signUp session", signOutErr);
        }
      }

      const alreadyRegistered = (data?.user?.identities?.length ?? 0) === 0;

      return {
        status: alreadyRegistered ? "already_registered" : "confirmation_sent",
        message: alreadyRegistered
          ? "Account already exists. Please sign in or reset your password."
          : "Check your email to verify your account.",
        user: data?.user ?? null,
        session: null,
      };
    },
    [emailConfirmationRedirectTo]
  );

  const signUpWithEmail = useCallback(
    async (email, password, role) => signUp({ email, password, role }),
    [signUp]
  );

  const signIn = useCallback(async ({ email, password }) => {
    if (!supabase) throw new Error("Supabase not configured");
    setAuthError(null);

    if (signInInFlightRef.current) {
      console.warn("[AUTH] signIn: already in flight; returning existing promise");
      return signInInFlightRef.current;
    }

    if (isDev) console.log("[AUTH] signIn: attempting login");

    const run = (async () => {
      try {
        // Use 30s timeout for auth token to account for transient outages + retry backoff
        // (3 retries × exponential backoff: 300ms, 900ms, 2000ms = ~3300ms max)
        const timeoutMs = 30000;
        const startedAt = (globalThis?.performance?.now?.() ?? Date.now());

        console.log("[AUTH] signIn: calling supabase.auth.signInWithPassword", {
          onLine: typeof navigator !== "undefined" ? navigator.onLine : null,
          supabaseOrigin: SUPABASE_URL_ORIGIN,
          timeoutMs,
        });

        const supabaseCall = supabase.auth.signInWithPassword({ email, password });
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`signIn timeout after ${timeoutMs}ms`)), timeoutMs)
        );

        // IMPORTANT: timeout wraps ONLY the Supabase auth call
        const { data, error } = await Promise.race([supabaseCall, timeoutPromise]);
        const elapsedMs = Math.round((globalThis?.performance?.now?.() ?? Date.now()) - startedAt);

        console.log("[AUTH] signIn: supabase resolved", {
          elapsedMs,
          hasError: !!error,
          hasSession: !!data?.session,
        });

        if (error) {
          console.error("[AUTH] signIn: error", error);
          setAuthError(error);
          throw error;
        }

        setSession(data?.session ?? null);
        setUser(data?.session?.user ?? null);
        console.log("[AUTH] signIn: success", { userId: data?.session?.user?.id });
        return data;
      } catch (err) {
      // Handle timeout
      if (String(err?.message || "").toLowerCase().includes("timeout")) {
        console.warn("[AUTH] signIn timed out", {
          onLine: typeof navigator !== "undefined" ? navigator.onLine : null,
          supabaseOrigin: SUPABASE_URL_ORIGIN,
        });
        setAuthError(err);
        throw err;
      }

      // Handle network/fetch errors with clear message
      const msg = String(err?.message || err?.name || "");
      const isFetchError = msg.includes("Failed to fetch") || msg.includes("TypeError") || err?.name === "TypeError";
      const isAuthRetryable = err?.name === "AuthRetryableFetchError" || msg.toLowerCase().includes("authretryable");
      
      if (isFetchError || isAuthRetryable) {
        const networkError = new Error(
          "Supabase unreachable from browser (network/extension/VPN/DNS). Check your network, disable extensions, or verify DNS."
        );
        if (isDev) {
          console.error("[AUTH] Network error detected:", {
            originalMessage: err?.message || err?.name,
            isFetchError,
            isAuthRetryable,
            onLine: typeof navigator !== "undefined" ? navigator.onLine : null,
          });
        }
        setAuthError(networkError);
        throw networkError;
      }
      throw err;
      }
    })();

    signInInFlightRef.current = run;
    try {
      return await run;
    } finally {
      signInInFlightRef.current = null;
    }
  }, []);

  const ensureFreshSessionWithState = useCallback(async ({ reason } = {}) => {
    const nextSession = await ensureFreshSession({ reason });
    if (nextSession) {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
    }
    return nextSession;
  }, []);

  // LEGACY WRAPPER - kept for backward compatibility
  const legacySignIn = useCallback(async ({ email, password }) => {
    const result = await signIn({ email, password });

    // Error and success already handled in try-catch above
    return result;
  }, [signIn]);

  const signInWithPassword = useCallback(
    async (email, password) => signIn({ email, password }),
    [signIn]
  );

  const signInWithMagicLink = useCallback(async (email) => {
    if (!supabase) throw new Error("Supabase not configured");
    setAuthError(null);

    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
      },
    });

    if (error) {
      setAuthError(error);
      throw error;
    }
    return data;
  }, []);

  const requestOtpCode = useCallback(async (email) => {
    if (!supabase) throw new Error("Supabase not configured");
    setAuthError(null);

    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
    });

    if (error) {
      setAuthError(error);
      throw error;
    }
    return data;
  }, []);

  const verifyOtpCode = useCallback(async (email, token) => {
    if (!supabase) throw new Error("Supabase not configured");
    setAuthError(null);

    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: "email",
    });

    if (error) {
      setAuthError(error);
      throw error;
    }
    return data;
  }, []);

  const attachPasswordToCurrentUser = useCallback(
    async (newPassword) => {
      if (!user?.id) throw new Error("No logged-in user to update password");
      if (!supabase) throw new Error("Supabase not configured");
      setAuthError(null);

      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        setAuthError(error);
        throw error;
      }
      return data;
    },
    [user?.id]
  );

  const sendPasswordResetEmail = useCallback(
    async (email) => {
      if (!supabase) throw new Error("Supabase not configured");
      setAuthError(null);

      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: passwordResetRedirectTo,
      });

      if (error) {
        setAuthError(error);
        throw error;
      }
      return data;
    },
    [passwordResetRedirectTo]
  );

  const updatePassword = useCallback(async (newPassword) => {
    if (!supabase) throw new Error("Supabase not configured");
    setAuthError(null);

    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      setAuthError(error);
      throw error;
    }
    return data;
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;

    debugAuthLog("signOut", { currentUserId: user?.id || null });
    await supabase.auth.signOut();

    setSession(null);
    setUser(null);
    setProfile(null);
    setStudioProfile(null);
    setVisitorProfile(null);
    setUserProfile(null);
    setOnboardingStatus(null);
    setAuthError(null);
  }, []);

  const getCurrentUser = useCallback(async () => {
    if (!supabase) return null;
    setAuthError(null);

    const { data, error } = await supabase.auth.getUser();
    if (error) {
      setAuthError(error);
      throw error;
    }
    setUser(data?.user ?? null);
    return data?.user ?? null;
  }, []);

  const mergedProfile = useMemo(() => {
    const isMeaningful = (value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    };

    const merged = { ...(profile || {}) };

    const apply = (source) => {
      if (!source) return;
      for (const [key, value] of Object.entries(source)) {
        if (isMeaningful(value)) merged[key] = value;
      }
    };

    apply(visitorProfile);
    apply(studioProfile);
    return merged;
  }, [profile, visitorProfile, studioProfile]);

  const value = {
    user,
    userId: user?.id || null,
    email: user?.email || null,
    session,

    profile: mergedProfile,
    studioProfile,
    visitorProfile,
    profileError,
    userProfile,
    onboardingStatus,
    authLoading,
    isAuthReady,
    authReady: isAuthReady,
    authError,
    authDegraded,

    refreshProfile: () => fetchProfiles(user?.id),
    refreshUserProfile: () => fetchProfiles(user?.id),
    upsertUserProfile,

    signUp,
    signUpWithEmail,
    signIn,
    signInWithPassword,
    signInWithMagicLink,
    requestOtpCode,
    verifyOtpCode,
    sendPasswordResetEmail,
    attachPasswordToCurrentUser,
    updatePassword,
    signOut,
    forceSignOutOnAuthError,
    userNeedsEmailConfirmation,
    getCurrentUser,

    isSignedIn: !!user,

    // On-demand auth refresh (used by network retry helpers)
    ensureFreshSession: ensureFreshSessionWithState,

    // Promo exposure for UI
    promo: buildPromoState(userProfile || {}),
    hasFullAccess: (() => {
      const p = buildPromoState(userProfile || {});
      if (p?.isLifetime && p?.discountPercent === 100) return true;
      if (p?.isActive && (p?.discountPercent || 0) > 0) return true;
      return false;
    })(),

    // Dev-only helper: clear session and localStorage for debugging
    devClearSession: isDev ? async () => {
      try {
        await supabase?.auth?.signOut?.();
      } catch (e) {
        console.warn("[AUTH] devClearSession: signOut failed", e);
      }
      try {
        // Remove Supabase auth token from localStorage
        if (typeof localStorage !== "undefined") {
          const keys = Object.keys(localStorage);
          keys.forEach(k => {
            if (k.includes("auth-token") || k.includes("supabase")) {
              localStorage.removeItem(k);
            }
          });
        }
      } catch (e) {
        console.warn("[AUTH] devClearSession: localStorage cleanup failed", e);
      }
      setSession(null);
      setUser(null);
      setProfile(null);
      setAuthError(null);
      console.log("[AUTH] devClearSession: session cleared");
    } : null,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
